/**
 * Broker Account Service
 * Handles broker account OAuth flow and registration
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this service only orchestrates OAuth flow.
 * All broker account operations are handled by backend Broker Integration Service.
 */

import { api } from '../../../api/client';

export interface BrokerRegistrationParams {
  broker_type: 'zerodha' | 'angelone' | 'upstox' | 'dhan' | 'groww' | 'fyers';
  account_name: string;
  account_id: string;
  paper_trading?: boolean;
  real_time_data_enabled?: boolean;
  auto_sync_enabled?: boolean;
  redirect_uri?: string;
  /** Kite Connect API key (required for Zerodha). Create app at kite.zerodha.com */
  api_key?: string;
  /** Kite Connect API secret (required for Zerodha) */
  api_secret?: string;
}

export interface BrokerRegistrationResult {
  registration_id: string;
  authorization_url?: string;
  status: 'pending' | 'completed' | 'failed';
}

/**
 * Initiate broker account registration and OAuth flow
 * Returns authorization URL to redirect user to broker OAuth page
 */
export async function initiateBrokerRegistration(
  params: BrokerRegistrationParams
): Promise<BrokerRegistrationResult> {
  try {
    const response = await api.brokerRegistration({
      broker_type: params.broker_type,
      account_name: params.account_name,
      account_id: params.account_id,
      paper_trading: params.paper_trading,
      real_time_data_enabled: params.real_time_data_enabled,
      auto_sync_enabled: params.auto_sync_enabled,
      api_key: params.api_key,
      api_secret: params.api_secret,
    });

    // Backend returns registration_id and oauth_url in response
    const registrationId = response.data.registration_id || '';
    const oauthUrl = response.data.oauth_url;
    
    return {
      registration_id: registrationId,
      authorization_url: oauthUrl,
      status: 'pending',
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to initiate broker registration';
    throw new Error(errorMessage);
  }
}

/**
 * Check registration status
 */
export async function checkRegistrationStatus(
  registrationId: string
): Promise<BrokerRegistrationResult> {
  try {
    const response = await api.brokerRegistrationStatus(registrationId);
    return {
      registration_id: registrationId,
      status: (response.data.status as 'pending' | 'completed' | 'failed') || 'pending',
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to check registration status';
    throw new Error(errorMessage);
  }
}

/**
 * Handle OAuth callback (called by backend when broker redirects)
 * This is typically handled by backend, but frontend may need to poll status
 */
export async function handleOAuthCallback(params: {
  brokerType: string;
  state?: string;
  code?: string;
  oauthState?: string;
}): Promise<void> {
  try {
    await api.brokerRegistrationCallback(
      params.brokerType,
      params.state,
      params.oauthState,
      params.code
    );
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to handle OAuth callback';
    throw new Error(errorMessage);
  }
}

/**
 * Deregister (delete) a broker account
 * Permanently deletes a broker account by registration ID
 */
export async function deregisterBrokerAccount(
  registrationId: string
): Promise<{ success: boolean; id: string; message: string }> {
  try {
    const response = await api.brokerRegistrationDeregister(registrationId);
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to deregister broker account';
    throw new Error(errorMessage);
  }
}

/**
 * Reconnect a broker account
 * Routes to automated TOTP login or OAuth flow based on account auth_mode
 */
export async function reconnectBrokerAccount(
  registrationId: string,
  forceRefresh: boolean = false
): Promise<{ 
  oauth_url?: string | null; 
  registration_id: string; 
  is_reusing_existing: boolean; 
  account_status: string; 
  message: string;
  auth_mode?: string;
  is_connected?: boolean;
  connection_status?: string;
  reconnected_at?: string;
  token_refreshed?: boolean;
  status?: string;
}> {
  try {
    const response = await api.brokerRegistrationReconnect({
      registration_id: registrationId,
      force_refresh: forceRefresh
    });
    return response.data as any;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to reconnect broker account';
    throw new Error(errorMessage);
  }
}

/**
 * Register broker account with TOTP credentials
 * Stores credentials securely and performs automated authentication
 */
export async function registerBrokerAccountWithTOTP(params: {
  broker_type?: string;
  account_name: string;
  account_id: string; // Generic account ID (replaces zerodha_user_id)
  password: string; // For Zerodha: password, For Groww: TOTP token
  totp_secret: string;
  paper_trading?: boolean;
  real_time_data_enabled?: boolean;
  auto_sync_enabled?: boolean;
  /** Kite Connect API key (required for Zerodha) */
  api_key?: string;
  /** Kite Connect API secret (required for Zerodha) */
  api_secret?: string;
}): Promise<{
  registration_id: string;
  status: string;
  broker_account_id: string;
  auth_mode: string;
  message?: string;
}> {
  try {
    // Send account_id directly - backend expects account_id (maps to broker_account_id in DB)
    const response = await api.brokerRegistrationTOTP({
      broker_type: params.broker_type || 'zerodha',
      account_name: params.account_name,
      account_id: params.account_id,
      password: params.password,
      totp_secret: params.totp_secret,
      paper_trading: params.paper_trading || false,
      real_time_data_enabled: params.real_time_data_enabled !== undefined ? params.real_time_data_enabled : true,
      auto_sync_enabled: params.auto_sync_enabled !== undefined ? params.auto_sync_enabled : true,
      api_key: params.api_key,
      api_secret: params.api_secret,
    });
    // Convert null message to undefined to match return type
    const data = response.data;
    return {
      ...data,
      message: data.message ?? undefined,
    };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to register broker account with TOTP';
    throw new Error(errorMessage);
  }
}

/**
 * Update TOTP credentials for an existing account
 * Requires account to be in TOTP mode (auth_mode='totp_automated')
 */
export async function updateTOTPCredentials(
  registrationId: string,
  password: string,
  totp_secret: string
): Promise<{
  registration_id: string;
  status: string;
  updated_at: string;
  message: string;
}> {
  try {
    const response = await api.brokerRegistrationUpdateTOTPCredentials(registrationId, {
      password,
      totp_secret,
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to update TOTP credentials';
    throw new Error(errorMessage);
  }
}

/**
 * Migrate existing OAuth account to TOTP-based automated authentication
 * Requires account to be in OAuth mode (auth_mode='oauth')
 */
export async function migrateToTOTP(
  registrationId: string,
  password: string,
  totp_secret: string
): Promise<{
  registration_id: string;
  status: string;
  auth_mode: string;
  migrated_at: string;
  message: string;
}> {
  try {
    const response = await api.brokerRegistrationMigrateToTOTP(registrationId, {
      password,
      totp_secret,
    });
    return response.data;
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to migrate account to TOTP';
    throw new Error(errorMessage);
  }
}

